import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Image, X, Upload, AlertCircle } from 'lucide-react';
import { DocumentAttachment, DocumentType } from '../types';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface DocumentUploaderProps {
  documents: DocumentAttachment[];
  onAddDocument: (document: DocumentAttachment) => void;
  onRemoveDocument: (documentId: string) => void;
}

const DocumentUploader: React.FC<DocumentUploaderProps> = ({ 
  documents, 
  onAddDocument, 
  onRemoveDocument 
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setError(null);
    
    for (const file of acceptedFiles) {
      try {
        setIsUploading(true);

        // Check file type
        const isPdf = file.type === 'application/pdf';
        const isImage = file.type.startsWith('image/');
        
        if (!isPdf && !isImage) {
          setError('Only PDF and image files are allowed');
          continue;
        }
        
        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setError('File size must be less than 5MB');
          continue;
        }

        // Generate a unique file path
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `session-documents/${fileName}`;

        // Upload file to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('mbfc-data')
          .upload(filePath, file);

        if (uploadError) {
          throw uploadError;
        }

        if (!uploadData?.path) {
          throw new Error('Upload failed - no path returned');
        }

        // Get public URL for the uploaded file
        const { data: { publicUrl } } = supabase.storage
          .from('mbfc-data')
          .getPublicUrl(uploadData.path);

        // Create document object
        const documentType: DocumentType = isPdf ? 'pdf' : 'image';
        const newDocument: DocumentAttachment = {
          id: Date.now().toString(),
          name: file.name,
          type: documentType,
          url: publicUrl,
          size: file.size,
          uploadedAt: new Date().toISOString()
        };
        
        onAddDocument(newDocument);
        toast.success('Document uploaded successfully');
      } catch (err: any) {
        console.error('Error uploading document:', err);
        setError(err.message || 'Failed to upload document. Please try again.');
        toast.error('Failed to upload document');
      } finally {
        setIsUploading(false);
      }
    }
  }, [onAddDocument]);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif']
    },
    disabled: isUploading
  });
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const getDocumentIcon = (type: DocumentType) => {
    return type === 'pdf' ? <FileText size={20} className="text-red-500" /> : <Image size={20} className="text-navy-500" />;
  };

  const handleRemove = async (doc: DocumentAttachment) => {
    try {
      // Extract file path from URL
      const url = new URL(doc.url);
      const filePath = url.pathname.split('/').slice(3).join('/');

      // Delete from Supabase Storage
      const { error: deleteError } = await supabase.storage
        .from('mbfc-data')
        .remove([filePath]);

      if (deleteError) {
        throw deleteError;
      }

      onRemoveDocument(doc.id);
      toast.success('Document removed successfully');
    } catch (err: any) {
      console.error('Error removing document:', err);
      toast.error(err.message || 'Failed to remove document');
    }
  };

  return (
    <div className="space-y-4">
      <div 
        {...getRootProps()} 
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-navy-500 bg-navy-50' : 'border-gray-300 hover:border-navy-400'
        } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <Upload size={36} className="mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-600">
          {isUploading ? 'Uploading...' : 
            isDragActive ? 
              'Drop the files here...' : 
              'Drag & drop PDF or image files here, or click to select files'
          }
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Maximum file size: 5MB
        </p>
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start">
          <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      
      {documents.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Attached Documents</h3>
          <ul className="space-y-2">
            {documents.map(doc => (
              <li key={doc.id} className="flex items-center justify-between bg-white p-3 rounded-md border border-gray-200">
                <div className="flex items-center">
                  {getDocumentIcon(doc.type)}
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-800">{doc.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(doc)}
                  className="text-gray-400 hover:text-red-500"
                  aria-label="Remove document"
                >
                  <X size={18} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DocumentUploader;